import {
  getRTFInformation,
  rtfFromText,
} from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { CarouselThumbnailLink } from "../elements/CarouselThumbnailLink";
import { LinkInformation } from "../elements/Links";
import { SimpleLink } from "../elements/SimpleLink";
import { ThumbnailLink } from "../elements/ThumbnailLink";
import { lookupColourString } from "../lookups";
import { AccordionLink } from "./AccordionLink";
import { AccordionRow } from "./AccordionRow";
import {
  buildContentBlock,
  buildLinksFromPageLinkFull,
  buildThumbnailLink,
  calcBlockIndex,
  getAlt,
  getImage,
  getLink,
  getLinkForPage,
  getPageCategory,
  getSvg,
} from "./Blocks";
import { Carousel } from "./Carousel";
import { Collection, CollectionContent, CollectionProps } from "./Collection";
import { List } from "./List";
import { PageCarousel } from "./PageCarousel";
import { PageCollection } from "./PageCollection";
import { Person } from "./Person";
import { PersonCTA } from "./PersonCTA";
import { RowCollection } from "./RowCollection";
import { ServicesCarousel } from "./ServicesCarousel";
import { ServicesSummarySlide } from "./ServicesSummarySlide";
import { SlidePageLink } from "./SlidePageLink";
import { SummarySlide, SummarySlideContent } from "./SummarySlide";
import { TeamCTA } from "./TeamCTA";
import { TeamCollection } from "./TeamCollection";

export function calculateCollectionType(
  collectionType?: Queries.GraphCMS_CollectionType | null,
): React.FC<CollectionProps> {
  switch (collectionType) {
    case "Team":
      return TeamCollection;
    case "PageCarousel":
      return PageCarousel;
    case "Carousel":
      return Carousel;
    case "NumberedList":
      return List;
    case "TeamCTA":
      return TeamCTA;
    case "PageCollection":
      return PageCollection;
    case "AccordionLink":
    case "AccordionExpand":
      return RowCollection;
    case "ServicesCarousel":
      return ServicesCarousel;
  }
  return Collection;
}

function buildSimpleLinkForPage(page: Queries.LinkedPageFragment | null) {
  if (page) {
    const link = getLinkForPage(page);
    const linkTitle = page.linkTitle;
    if (link) {
      return (
        <SimpleLink key={page.id} to={link}>
          {linkTitle ?? <Trans>Discover more</Trans>}
        </SimpleLink>
      );
    }
  }
  return undefined;
}

function buildSlidePageLink(
  slidePageLink: Queries.PageLinkFullFragment,
  slideHeading?: string,
) {
  return (
    <SlidePageLink
      key={slidePageLink.id}
      image={getImage(slidePageLink.image)}
      heading={slideHeading ? slideHeading : slidePageLink.tagline}
      alt={getAlt(slidePageLink.image, slidePageLink.title || "Link")}
      id={slidePageLink.id}
      title={slidePageLink.title}
      message={getRTFInformation(slidePageLink.message)}
      link={getLink(slidePageLink)}
      linkTitle={slidePageLink.linkTitle}
    />
  );
}

function buildSlideFromPage(
  page: Queries.LinkedPageFragment,
  slideHeading?: string,
) {
  return (
    <SlidePageLink
      key={page.id}
      image={getImage(page.heroImage)}
      heading={slideHeading}
      alt={getAlt(page.heroImage, page.title || "Page")}
      id={page.id}
      title={page.title}
      message={getRTFInformation(page.summary)}
      link={getLinkForPage(page)}
      linkTitle={page.linkTitle}
    />
  );
}

function buildPerson({
  id,
  name,
  image,
  jobPosition,
  email,
  phone,
  description,
}: Queries.PersonFragment) {
  return (
    <Person
      key={id}
      name={name}
      image={getImage(image)}
      jobTitle={jobPosition}
      email={email}
      phone={phone}
      description={description}
    />
  );
}

function buildCarouselThumbnailLink(
  link: Queries.PageLinkFullFragment,
): JSX.Element {
  return (
    <CarouselThumbnailLink
      key={link.id}
      image={getImage(link.image)}
      alt={getAlt(link.image, link.title || "Link")}
      title={link.title}
      tagline={link.tagline}
      message={getRTFInformation(link.message)}
      links={buildLinksFromPageLinkFull(link)}
    />
  );
}

function buildAccordionLinkFromPage(page: Queries.LinkedPageFragment) {
  return (
    <AccordionLink
      key={page.id}
      title={page.title}
      link={getLinkForPage(page)}
    />
  );
}

function buildAccordionRowFromContentBlock(
  block: Queries.ContentBlockFragment,
) {
  return (
    <AccordionRow
      key={block.id}
      title={block.title}
      image={getImage(block.image)}
      alt={getAlt(block.image, block.title || "AccordionRow")}
      message={getRTFInformation(block.message)}
    />
  );
}

function buildLinksFromDirectPage(
  page: Queries.DirectPageFragment,
): Array<LinkInformation> {
  return [{ title: page.linkTitle ?? "", internal: getLinkForPage(page) }];
}

export function buildThumbnailLinkFromPage(
  page: Queries.DirectPageFragment,
): JSX.Element {
  const link = page.pageLinks?.length ? page.pageLinks[0] : undefined;
  const image = page.heroImage ?? link?.image;
  const title = page.title;
  return (
    <ThumbnailLink
      key={page.id}
      image={getImage(image)}
      alt={getAlt(image, title)}
      title={title}
      tagline={getPageCategory(page) ?? "Missing"}
      message={getRTFInformation(page.summary)}
      links={buildLinksFromDirectPage(page)}
    />
  );
}

export function filterPages(
  allPages: ReadonlyArray<Queries.DirectPageFragment>,
  pageTypeFilter: Queries.GraphCMS_PageType,
  pageFilterCount: number,
): Array<Queries.DirectPageFragment> {
  return allPages
    .filter((p) => p.pageType === pageTypeFilter)
    .slice(0, pageFilterCount);
}

export function buildThumbnailLinksFromPages(
  allPages: ReadonlyArray<Queries.DirectPageFragment>,
  pageTypeFilter: Queries.GraphCMS_PageType,
  pageFilterCount: number,
): Array<JSX.Element> {
  return filterPages(allPages, pageTypeFilter, pageFilterCount).map(
    buildThumbnailLinkFromPage,
  );
}

function buildPersonCTA(
  { id, name, image, jobPosition, email, phone }: Queries.PersonFragment,
  index: number,
) {
  return (
    <PersonCTA
      key={id}
      name={name}
      image={getImage(image)}
      jobTitle={jobPosition}
      email={email}
      phone={phone}
      index={index}
    />
  );
}

function buildSingleContent(
  collectionType: Queries.GraphCMS_CollectionType | null,
  content: Queries.CollectionFragment["contents"][0],
  index: number,
  blockTypeCount: number,
  page?: Queries.PageFragment,
  collectionSize?: number,
): CollectionContent {
  const slideIndexText = `${index + 1}`.padStart(2, "0");
  const typename = content.__typename;
  switch (typename) {
    case "GraphCMS_PageLink":
      switch (collectionType) {
        case "PageCarousel":
          return { content: buildCarouselThumbnailLink(content) };
        case "PageCollection":
          return { content: buildThumbnailLink(content) };

        default:
          return {
            content: buildSlidePageLink(content, slideIndexText),
          };
      }

    case "GraphCMS_Person":
      switch (collectionType) {
        case "TeamCTA":
          return { content: buildPersonCTA(content, index) };
      }
      return {
        content: buildPerson(content),
      };
    case "GraphCMS_ContentBlock":
      switch (collectionType) {
        case "AccordionExpand":
          return { content: buildAccordionRowFromContentBlock(content) };
      }

      return {
        content: buildContentBlock(
          content,
          index,
          blockTypeCount,
          collectionType,
          page,
          collectionSize,
        ),
        background: content.backgroundColour,
        contentBackgroundClassName: content.backgroundColour
          ? lookupColourString(content.backgroundColour, "bg")
          : undefined,
        accentColourClassName: content.backgroundColour
          ? lookupColourString(content.backgroundColour, "slides")
          : undefined,
        notSelectedAccentColourClassName: content.backgroundColour
          ? lookupColourString(content.backgroundColour, "slidesNotSelected")
          : undefined,
        textColourClassName: content.backgroundColour
          ? lookupColourString(content.backgroundColour, "textForBg")
          : undefined,
      };

    case "GraphCMS_Page":
      switch (collectionType) {
        case "PageCarousel":
          return { content: buildThumbnailLinkFromPage(content) };
        case "PageCollection":
          return { content: buildThumbnailLinkFromPage(content) };
        case "AccordionLink":
          return { content: buildAccordionLinkFromPage(content) };
        case "Carousel":
          return { content: buildSlideFromPage(content, slideIndexText) };

        default:
          return { content: buildSlideFromPage(content, slideIndexText) };
      }
    default:
      return {
        content: <h1>{typename} TBD</h1>,
      };
  }
}

export function buildContents(
  collection: Queries.CollectionFragment,
  page?: Queries.PageFragment,
): Array<CollectionContent> {
  const { collectionType } = collection;
  if (!collection.contents) return [];
  const blockTypeCount: Record<string, number> = {};

  return collection.contents.map((c, index) => {
    const typeIndex =
      c.__typename === "GraphCMS_ContentBlock"
        ? calcBlockIndex(c.contentBlockType, blockTypeCount)
        : 1;
    return buildSingleContent(
      collectionType,
      c,
      index,
      typeIndex,
      page,
      collection.contents.length,
    );
  });
}

export const BlockCollection: React.FC<{
  page?: Queries.PageFragment;
  collection: Queries.CollectionFragment;
}> = ({ page, collection }) => {
  const [slideIndex, setSlideIndex] = useState(0);

  const contents = useMemo(() => {
    const contents = buildContents(collection, page);
    if (collection.showSummary) {
      const summarySlide = buildSummarySlide(collection, setSlideIndex);
      contents.splice(0, 0, { content: summarySlide, background: "Hyacinth" });
    }
    return contents;
  }, [page, collection]);

  const CollectionImplementation = calculateCollectionType(
    collection.collectionType,
  );

  return (
    <CollectionImplementation
      slideIndex={slideIndex}
      setSlideIndex={setSlideIndex}
      title={collection.displayTitle}
      showTitle={collection.showTitle}
      key={collection.id}
      forceScroll={collection.forceScroll}
      message={getRTFInformation(collection.message)}
      shape={
        getSvg(collection.backgroundShape) || getSvg(collection.backgroundImage)
      }
      image={getImage(collection.backgroundImage)}
      alt={getAlt(collection.backgroundImage, collection.name || "Image")}
      className={classNames(
        collection.backgroundColour &&
          lookupColourString(collection.backgroundColour, "bg"),
        collection.backgroundColour &&
          lookupColourString(collection.backgroundColour, "textForBg"),
      )}
      contents={contents}
      archiveLink={buildSimpleLinkForPage(collection.page)}
      pageTypeFilter={collection.pageTypeFilter}
      pageFilterCount={collection.pageFilterCount}
      showFilter={collection.showFilter}
      showLargeFirstResult={collection.showLargeFirstResult}
      contactForm={collection.contactForm}
    />
  );
};

function buildSummarySlideContent(
  content: Queries.CollectionFragment["contents"][0],
) {
  switch (content.__typename) {
    case "GraphCMS_ContentBlock":
      return {
        title: content.title,
        message: getRTFInformation(content.message),
        id: content.id,
      } as SummarySlideContent;
    case "GraphCMS_Page":
      return {
        title: content.title,
        message: getRTFInformation(content.summary),
        id: content.id,
      } as SummarySlideContent;
    case "GraphCMS_PageLink":
      return {
        title: content.title,
        message: getRTFInformation(content.message),
        id: content.id,
      } as SummarySlideContent;
    case "GraphCMS_Person":
      return {
        title: content.name,
        message: content.jobPosition
          ? rtfFromText(content.jobPosition)
          : undefined,
        id: content.id,
      } as SummarySlideContent;
    default:
      console.error(`No content`);
  }
  return undefined;
}

function buildSummarySlide(
  collection: Queries.CollectionFragment,
  setSlideIndex: (index: number) => void,
) {
  const SummarySlideImplementation =
    collection.collectionType === "ServicesCarousel"
      ? ServicesSummarySlide
      : SummarySlide;
  return (
    <SummarySlideImplementation
      key={collection.id}
      onNavigate={setSlideIndex}
      title={collection.name}
      message={getRTFInformation(collection.message)}
      shape={getSvg(collection.backgroundShape)}
      slides={
        collection.contents
          .map(buildSummarySlideContent)
          .filter((s) => s) as Array<SummarySlideContent>
      }
    />
  );
}
